import { usePageMeta, useFAQSchema } from '@/hooks/useSEO';
import {
  PageHero, TrustBox, InfoCallout, WarnCallout, DataTable, FrameworkCTA, SectionH2, ILink, useBreadcrumbSchema,
} from './_shared';

const FAQS = [
  { q: 'How much is the official QVP verification fee?', a: 'The government portal fee for QVP verification is USD 186 per submission, payable online during document upload.' },
  { q: 'Can a candidate undergo Wafid medical tests before QVP clearance?', a: 'Yes. Wafid medical clearance and QVP credential verification can run in parallel streams. Final visa stamping requires both to be completed.' },
];

const PATH = '/services/saudi-work-visa-qvp-attestation/';

/* SPOKE 1 — Work Visa & QVP Attestation */
export default function SaudiWorkVisaQVPPage() {
  usePageMeta(
    'Saudi Arabia Work Visa & QVP Processing Guide 2026 | Zafco Services',
    'End-to-end guide on Saudi Work Visa processing, QVP qualification verification, Wafid GAMCA medicals, SSCO codes, and consular stamping.',
    { canonical: PATH },
  );
  useFAQSchema(FAQS);
  useBreadcrumbSchema([
    { name: 'Home', path: '/' },
    { name: 'Saudi Visa Framework 2026', path: '/saudi-visa-framework-2026/' },
    { name: 'Work Visa & QVP Attestation', path: PATH },
  ]);

  return (
    <article data-testid="saudi-qvp-root" className="bg-white">
      <PageHero
        mid="Saudi Visa Framework 2026"
        crumb="Work Visa & QVP Attestation"
        title="Saudi Arabia Work Visa Processing & Qualification Verification Program (QVP) Guide (2026)"
      />

      <div className="container mx-auto px-6 max-w-5xl py-12">
        <TrustBox
          author="Zafco Overseas Visa Processing & Attestation Division"
          reviewer="Head of Middle East Mobility & Consular Affairs"
          standard="Saudi MHRSD, MOFA, and Saudi Standard Classification of Occupations (SSCO)"
        />

        <section id="work-visa-overview">
          <SectionH2 id="work-visa-overview-h">1. Overview of the KSA Employment Visa System</SectionH2>
          <p className="text-slate-700 leading-relaxed mb-4">
            A Saudi Work Visa permits a foreign national to enter the Kingdom of Saudi Arabia for lawful employment under a sponsoring entity. As outlined in our master{' '}
            <ILink to="/saudi-visa-framework-2026/" testId="qvp-link-hub">Saudi Arabia Visa Framework 2026</ILink>, foreign workforce deployment is governed by a strict two-step legal structure:
          </p>
          <ul className="list-disc pl-6 space-y-3 text-slate-700 leading-relaxed mb-4">
            <li><strong>Step 1: Employment Entry Visa:</strong> A single-entry authorization stamped on the passport by the Saudi Embassy/Consulate, permitting entry into Saudi Arabia (valid for 90 days from the issue date).</li>
            <li>
              <strong>Step 2: Iqama (Residence Permit):</strong> The long-term digital residence and work identity record issued post-arrival by Jawazat via Muqeem, which governs the employee's legal stay and rights. Ensure that contract compensation structures match{' '}
              <ILink to="/resources/saudi-end-of-service-gratuity-calculator/" testId="qvp-link-gratuity">Saudi End-of-Service Gratuity calculations</ILink> from day one.
            </li>
          </ul>
          <p className="text-slate-700 leading-relaxed mb-4">
            For role-wise eligibility across Engineer, Technician, Nurse, Driver, and Labour categories, see our{' '}
            <ILink to="/saudi-arabia/work-visa" testId="qvp-link-old-work">Saudi Work Visa role-specific requirements guide</ILink>.
          </p>
          <InfoCallout>
            <strong>Mandatory Employer Requirements Prior to Candidate Submission:</strong>
            <ol className="list-decimal pl-5 mt-2 space-y-1.5">
              <li>An active, approved <strong>Visa Block / Quota</strong> issued by MHRSD for the precise occupation code.</li>
              <li>Compliant standing in the <strong>Nitaqat</strong> system (Platinum or High Green band).</li>
              <li>Validated <strong>Wakala</strong> (Delegation Authorization) issued through MOFA authorizing Zafco as the agency of record.</li>
            </ol>
          </InfoCallout>
        </section>

        <section id="qvp-deep-dive">
          <SectionH2 id="qvp-deep-dive-h">2. Qualification Verification Program (QVP) Mandate</SectionH2>
          <p className="text-slate-700 leading-relaxed mb-4">
            The Qualification Verification Program (QVP) is a non-negotiable administrative hurdle for foreign professionals seeking a Saudi Work Visa. The program applies across all economic sectors, requiring mandatory verification of academic degrees and work experience before visa stamping can be finalized.
          </p>
          <h3 className="text-xl font-bold text-[#000080] font-syne mt-6 mb-3">Key QVP Parameters & Execution Workflow:</h3>
          <ul className="list-disc pl-6 space-y-2 text-slate-700 leading-relaxed">
            <li><strong>Objective:</strong> To match candidates' verified credentials with the <strong>Saudi Standard Classification of Occupations (SSCO)</strong>.</li>
            <li><strong>Processing Time:</strong> Typically 15 to 21 business days, depending on home-country institutional responsiveness.</li>
            <li><strong>Statutory Portal Fee:</strong> USD 186 (approx. SAR 700) per credential submission.</li>
            <li><strong>Primary Source Verification (PSV):</strong> Conducted through authorized partners (e.g., DataFlow) contacting issuing institutions directly.</li>
          </ul>
          <DataTable
            testId="qvp-stages-table"
            headers={['QVP Stage', 'Responsible Party', 'Operational Action']}
            rows={[
              [<strong>1. Registration & Occupation Mapping</strong>, 'Agency / Employer', 'Select exact SSCO occupation code on Qiwa / QVP portal matching quota block.'],
              [<strong>2. Document Upload & Fee Payment</strong>, 'Candidate / Zafco', 'Upload scanned final degree, transcripts, passport copy, and settle USD 186 fee.'],
              [<strong>3. Primary Source Verification (PSV)</strong>, 'QVP / DataFlow', 'Direct outreach to issuing university/board to verify enrollment and degree authenticity.'],
              [<strong>4. Report Issuance & System Sync</strong>, 'QVP Platform', 'Status updates to "Qualified". Results automatically transmit to MOFA servers.'],
              [<strong>5. Visa File Integration</strong>, 'Zafco Compliance Team', 'Attach QVP Accreditation Certificate to the physical consular stamping file.'],
            ]}
          />
          <WarnCallout>
            <strong>Common QVP Failure Points to Avoid:</strong>
            <ul className="list-disc pl-5 mt-2 space-y-1.5">
              <li>Name mismatches between passport and educational degree (requires official affidavit prior to submission).</li>
              <li>Submission of provisional degrees instead of final degree/diploma certificates.</li>
              <li>Unrecognized institutions or technical diplomas with less than 3 years of academic duration.</li>
            </ul>
          </WarnCallout>
        </section>

        <section id="step-by-step-process">
          <SectionH2 id="step-by-step-h">3. Complete Step-by-Step Deployment Lifecycle</SectionH2>
          <ol className="list-decimal pl-6 space-y-4 text-slate-700 leading-relaxed">
            <li><strong>Pre-Submission (Home Country):</strong> Initiate QVP clearance, book and complete Wafid (GAMCA) medical examination, complete VFS biometric enrollment, and complete MEA/Apostille attestation.</li>
            <li><strong>Consular Visa Stamping:</strong> Submit complete physical dossier to Saudi Embassy/Consulate via Zafco (~10–15 business days). Passport receives a 90-day single-entry work visa sticker.</li>
            <li><strong>Post-Arrival & Iqama Conversion (In KSA):</strong> Conduct local medical test and issue Iqama via Jawazat/Muqeem within <strong>90 days of arrival</strong>. Register employee on Absher.</li>
          </ol>
        </section>

        <section id="document-checklist">
          <SectionH2 id="document-checklist-h">4. Comprehensive Document Requirements Checklist</SectionH2>
          <DataTable
            testId="documents-table"
            headers={['From the Candidate', 'From the Saudi Sponsor']}
            rows={[
              [
                <ul className="list-disc pl-4 space-y-1.5">
                  <li>Original Passport (7+ months validity, 2 blank pages)</li>
                  <li>2 Passport-size photos (3.5 × 4.5 cm, white background)</li>
                  <li>Wafid Fit Medical Certificate</li>
                  <li>Biometric Enrollment Receipt</li>
                  <li>Attested Educational Certificates (QVP Verified)</li>
                  <li>Police Clearance Certificate (PCC)</li>
                </ul>,
                <ul className="list-disc pl-4 space-y-1.5">
                  <li>MOFA Visa Approval Number (Visa Block)</li>
                  <li>Electronic Wakala issued to Zafco</li>
                  <li>Qiwa Digital Employment Contract</li>
                  <li>Copy of Commercial Registration (CR)</li>
                </ul>,
              ],
            ]}
          />
        </section>

        <section id="timelines-jurisdiction">
          <SectionH2 id="timelines-h">5. Processing Timelines & Strategic Consular Channeling</SectionH2>
          <p className="text-slate-700 leading-relaxed">
            Operational delays frequently stem from jurisdiction mismatches. Where operationally feasible, employers are advised to issue visa block authorizations under the <strong>Mumbai Consulate Jurisdiction</strong> for Indian applicants to optimize processing turnarounds. Contract salary structures registered on Qiwa should anticipate statutory severance — see{' '}
            <ILink to="/resources/saudi-end-of-service-gratuity-calculator/" testId="qvp-link-gratuity-2">Saudi End-of-Service Gratuity rules</ILink>.
          </p>
          <DataTable
            testId="timelines-table"
            headers={['Stage', 'Estimated Operational Timeline', 'Key Dependencies']}
            rows={[
              [<strong>QVP Verification</strong>, '15 – 21 Business Days', 'University registrar response speed.'],
              [<strong>Wafid GAMCA Medical</strong>, '1 – 2 Business Days', 'Lab processing turnaround.'],
              [<strong>Biometric Enrollment</strong>, '1 Business Day', 'VFS appointment slot availability.'],
              [<strong>Consular Visa Stamping</strong>, '10 – 15 Business Days', 'Embassy/Consulate queue.'],
              [<strong>Total Estimated Deployment Cycle</strong>, <strong>25 – 35 Business Days</strong>, 'Assumes parallel processing of QVP and Medicals.'],
            ]}
          />
        </section>

        <FrameworkCTA />

        <section id="faqs">
          <SectionH2 id="faqs-h">Frequently Asked Questions</SectionH2>
          {FAQS.map((f) => (
            <div key={f.q} className="mb-5">
              <h3 className="text-base font-bold text-[#000080] mb-1.5">{f.q}</h3>
              <p className="text-slate-700 leading-relaxed text-sm">{f.a}</p>
            </div>
          ))}
        </section>
      </div>
    </article>
  );
}
